import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Printer, Search, RotateCcw, Download, Eye, Pencil, Save, X, Ban, MessageCircle, Repeat, Plus } from "lucide-react";
import { shareInvoiceWhatsApp } from "./invoicePdf";
import api, { inr } from "../lib/api";
import PrintableBill from "./PrintableBill";

const PAYMENT_MODES = ["cash", "upi", "card"];

export default function PosBills() {
  const [bills, setBills] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);       // bill being viewed
  const [settings, setSettings] = useState({});
  const [returnMode, setReturnMode] = useState(false);
  const [returnItems, setReturnItems] = useState({});
  const [exchangeMode, setExchangeMode] = useState(false);
  const [newItems, setNewItems] = useState([]);        // replacement items
  const [exSearch, setExSearch] = useState("");
  const [exResults, setExResults] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [voidPrompt, setVoidPrompt] = useState(null);   // { bill, reason }
  const [printing, setPrinting] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/pos/bills", { params: { q: query, status: statusFilter } }).then((r) => setBills(r.data));
  useEffect(() => { load(); api.get("/pos/settings").then((r) => setSettings(r.data || {})); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [query, statusFilter]);

  const view = async (b) => {
    const { data } = await api.get(`/pos/bills/${b.id}`);
    setSelected(data);
    setReturnMode(false);
    setReturnItems({});
    setExchangeMode(false);
    setNewItems([]);
    setExSearch("");
    setExResults([]);
    setEditMode(false);
    setEditDraft(null);
    return data;
  };

  // ---- WhatsApp the invoice (as a PDF) to the customer ----
  const waSend = () => { shareInvoiceWhatsApp(selected, settings); };

  // ---- Exchange ----
  const runExSearch = async (q) => {
    setExSearch(q);
    if (!q.trim()) return setExResults([]);
    try {
      const { data } = await api.get("/pos/product-search", { params: { q } });
      setExResults(Array.isArray(data) ? data.slice(0, 6) : []);
    } catch { setExResults([]); }
  };
  const addNewItem = (p) => {
    const size = (p.sizes && p.sizes[0]) || "";
    setNewItems((arr) => [...arr, { product_id: p.id, name: p.name, sku: p.sku || "", size, color: p.color || "", qty: 1, unit_price: p.price }]);
    setExSearch(""); setExResults([]);
  };
  const submitExchange = async () => {
    const return_items = Object.entries(returnItems).filter(([, v]) => Number(v.qty) > 0).map(([k, v]) => {
      const [product_id, size] = k.split("||");
      return { product_id, size, qty: Number(v.qty) };
    });
    if (!return_items.length) return toast.error("Select at least one item to return");
    if (!newItems.length) return toast.error("Add at least one replacement item");
    try {
      const { data } = await api.post("/pos/exchanges", {
        bill_id: selected.id, return_items,
        new_items: newItems.map((n) => ({ ...n, qty: Number(n.qty), unit_price: Number(n.unit_price) })),
      });
      const diff = data.difference;
      toast.success(diff > 0 ? `Exchange done — collect ${inr(diff)} from customer` : diff < 0 ? `Exchange done — refund ${inr(-diff)} to customer` : "Exchange done — even swap");
      setExchangeMode(false); setNewItems([]); setReturnItems({}); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Exchange failed"); }
  };

  const exReturnValue = useMemo(() => {
    if (!selected) return 0;
    const orig = {};
    (selected.items || []).forEach((i) => { orig[`${i.product_id}||${i.size || ""}`] = i.unit_price; });
    return Object.entries(returnItems).reduce((s, [k, v]) => s + (orig[k] || 0) * (Number(v.qty) || 0), 0);
  }, [returnItems, selected]);
  const exNewValue = useMemo(() => newItems.reduce((s, n) => s + Number(n.unit_price) * Number(n.qty), 0), [newItems]);

  const openEdit = (bill) => {
    const b = bill || selected;
    if (!b) return;
    setEditDraft({
      customer_id: b.customer_id || "",
      walkin_name: b.customer_name || "",
      walkin_phone: b.customer_phone || "",
      discount_type: b.discount_type || "none",
      discount_value: b.discount_value || 0,
      gst_enabled: !!b.gst_enabled,
      gst_rate: b.gst_rate || 5,
      gstin: b.gstin || "",
      payments: b.payments && b.payments.length ? b.payments : [{ mode: "cash", amount: b.total }],
      notes: b.notes || "",
    });
    setEditMode(true);
    setReturnMode(false);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editDraft,
        discount_value: Number(editDraft.discount_value) || 0,
        gst_rate: Number(editDraft.gst_rate) || 0,
        payments: editDraft.payments.filter((p) => Number(p.amount) > 0).map((p) => ({ ...p, amount: Number(p.amount) })),
      };
      const { data } = await api.put(`/pos/bills/${selected.id}`, payload);
      setSelected(data);
      setEditMode(false);
      toast.success(`Bill ${data.bill_no} updated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const submitVoid = async () => {
    if (!voidPrompt) return;
    try {
      const { data } = await api.post(`/pos/bills/${voidPrompt.bill.id}/void`, { reason: voidPrompt.reason });
      toast.success(`Bill ${data.bill_no} voided. Number kept in ledger.`);
      if (selected?.id === data.id) setSelected(data);
      setVoidPrompt(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Void failed"); }
  };

  const submitReturn = async () => {
    const items = Object.entries(returnItems).filter(([, v]) => Number(v.qty) > 0).map(([k, v]) => {
      const [product_id, size] = k.split("||");
      return { product_id, size, qty: Number(v.qty), reason: v.reason || "" };
    });
    if (!items.length) return toast.error("Select at least one item to return");
    try {
      await api.post("/pos/returns", { bill_id: selected.id, items, mode: "refund" });
      toast.success("Return processed & stock restored");
      setReturnMode(false); setReturnItems({}); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Return failed"); }
  };

  const doPrint = () => { setPrinting(true); setTimeout(() => { window.print(); setPrinting(false); }, 200); };

  const exportCsv = async () => {
    const url = process.env.REACT_APP_BACKEND_URL + "/api/pos/export/bills.csv";
    const token = localStorage.getItem("ambrai_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "bills.csv";
    link.click();
  };

  // Preview totals as user edits
  const previewTotals = useMemo(() => {
    if (!editDraft || !selected) return null;
    const items = selected.items || [];
    const subtotal = Math.max(0, items.reduce((s, i) => s + i.unit_price * i.qty - (i.line_discount || 0), 0));
    let discount = 0;
    if (editDraft.discount_type === "flat") discount = Math.min(Number(editDraft.discount_value) || 0, subtotal);
    else if (editDraft.discount_type === "percent") discount = Math.floor((subtotal * (Number(editDraft.discount_value) || 0)) / 100);
    const taxable = subtotal - discount;
    const cgst = editDraft.gst_enabled ? Math.floor(taxable * (Number(editDraft.gst_rate) / 2) / 100) : 0;
    const sgst = editDraft.gst_enabled ? Math.floor(taxable * (Number(editDraft.gst_rate) / 2) / 100) : 0;
    const total = taxable + cgst + sgst;
    const paid = editDraft.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return { subtotal, discount, taxable, cgst, sgst, total, paid, balance: total - paid };
  }, [editDraft, selected]);

  return (
    <div className="p-8" data-testid="pos-bills-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Bills</h1>
          <p className="text-sm text-slate-500">All past receipts. Click a row to view; edit, reprint, return or void from the detail view.</p>
        </div>
        <button onClick={exportCsv} className="flex items-center gap-2 px-4 py-2.5 border border-slate-800 rounded text-sm text-slate-300 hover:text-amber-400 hover:border-amber-500"><Download size={14} /> Export CSV</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search bill no, customer name or phone" className="w-full bg-slate-900 border border-slate-800 rounded pl-10 pr-4 py-3 text-sm text-slate-200" data-testid="pos-bills-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-slate-900 border border-slate-800 rounded px-3 py-3 text-sm text-slate-200 sm:w-52" data-testid="pos-bills-status-filter">
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial payment</option>
          <option value="unpaid">Unpaid</option>
          <option value="voided">Voided</option>
        </select>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-md overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-950 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Bill</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-right px-4 py-3">Items</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {bills.map((b) => (
              <tr key={b.id} className="hover:bg-slate-800/50" data-testid={`bill-row-${b.bill_no}`}>
                <td className="px-4 py-3 font-mono text-amber-400">{b.bill_no}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{new Date(b.created_at).toLocaleString("en-IN")}{b.edited_at && <span className="ml-1 text-[10px] text-blue-400" title={`Edited ${new Date(b.edited_at).toLocaleString("en-IN")}`}>· edited</span>}</td>
                <td className="px-4 py-3">
                  <p className="text-slate-200">{b.customer_name || "Walk-in"}</p>
                  <p className="text-xs text-slate-500 font-mono">{b.customer_phone}</p>
                </td>
                <td className="px-4 py-3 text-right text-slate-400">{b.items?.length || 0}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-100">
                  <span className={b.status === "voided" ? "line-through text-slate-600" : ""}>{inr(b.total)}</span>
                </td>
                <td className="px-4 py-3">
                  {b.status === "voided" ? (
                    <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-300 font-mono uppercase">voided</span>
                  ) : (
                    <span className={`text-xs px-2 py-1 rounded ${b.status === "paid" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>{b.status}</span>
                  )}
                  {b.has_return && <span className="ml-1 text-xs text-red-400">· returned</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => view(b)} className="p-2 text-slate-400 hover:text-amber-400" title="View" data-testid={`bill-view-${b.bill_no}`}><Eye size={15} /></button>
                    {b.status !== "voided" && (
                      <>
                        <button onClick={async () => { const d = await view(b); openEdit(d); }} className="p-2 text-slate-400 hover:text-blue-400" title="Edit" data-testid={`bill-edit-${b.bill_no}`}><Pencil size={15} /></button>
                        <button onClick={() => setVoidPrompt({ bill: b, reason: "" })} className="p-2 text-slate-400 hover:text-orange-400" title="Void (keeps bill in ledger)" data-testid={`bill-void-${b.bill_no}`}><Ban size={15} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {bills.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-slate-600">No bills yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-black/70 grid place-items-center p-3 sm:p-6 print:hidden" onClick={() => setSelected(null)}>
          <div className="bg-slate-900 border border-slate-800 rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-800 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-widest">Bill</p>
                <h2 className="text-xl font-semibold font-mono text-amber-400">{selected.bill_no}</h2>
              </div>
              <div className="flex gap-2 flex-wrap justify-end">
                {selected.status === "voided" && (
                  <div className="w-full mb-2 flex items-center gap-2 bg-slate-800 border border-orange-500/30 rounded px-3 py-2 text-xs text-orange-300">
                    <Ban size={13} />
                    <span>Voided{selected.void_reason && ` — ${selected.void_reason}`} · {selected.voided_at && new Date(selected.voided_at).toLocaleString("en-IN")}</span>
                  </div>
                )}
                {!editMode && !returnMode && !exchangeMode && selected.status !== "voided" && (
                  <>
                    <button onClick={waSend} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-300 hover:text-emerald-400 hover:border-emerald-500" data-testid="pos-whatsapp-btn"><MessageCircle size={14} /> WhatsApp</button>
                    <button onClick={() => openEdit()} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-300 hover:text-blue-400 hover:border-blue-500" data-testid="pos-edit-toggle"><Pencil size={14} /> Edit</button>
                    <button onClick={() => setExchangeMode(true)} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-300 hover:text-amber-400 hover:border-amber-500" data-testid="pos-exchange-toggle"><Repeat size={14} /> Exchange</button>
                    <button onClick={() => setReturnMode(true)} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-300 hover:text-red-400 hover:border-red-500" data-testid="pos-return-toggle"><RotateCcw size={14} /> Return</button>
                    <button onClick={() => setVoidPrompt({ bill: selected, reason: "" })} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-300 hover:text-orange-400 hover:border-orange-500" data-testid="pos-void-btn"><Ban size={14} /> Void</button>
                    <button onClick={doPrint} className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-slate-950 rounded text-sm font-medium hover:bg-amber-400"><Printer size={14} /> Print</button>
                  </>
                )}
                {selected.status === "voided" && (
                  <button onClick={doPrint} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-300 hover:text-amber-400 hover:border-amber-500"><Printer size={14} /> Print copy</button>
                )}
                {editMode && (
                  <>
                    <button onClick={() => setEditMode(false)} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-400 hover:text-slate-200"><X size={14} /> Cancel</button>
                    <button onClick={saveEdit} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded text-sm font-medium hover:bg-blue-400 disabled:opacity-50" data-testid="pos-edit-save"><Save size={14} /> {saving ? "Saving…" : "Save changes"}</button>
                  </>
                )}
                {returnMode && (
                  <button onClick={() => setReturnMode(false)} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-400 hover:text-slate-200"><X size={14} /> Cancel</button>
                )}
                {exchangeMode && (
                  <button onClick={() => { setExchangeMode(false); setNewItems([]); setReturnItems({}); }} className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 rounded text-sm text-slate-400 hover:text-slate-200"><X size={14} /> Cancel exchange</button>
                )}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Items — always read-only */}
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase">
                  <tr><th className="text-left py-2">Item</th><th className="text-center">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th>{(returnMode || exchangeMode) && <th className="text-right">{exchangeMode ? "Return" : "Return"}</th>}</tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {selected.items?.map((it, i) => {
                    const key = `${it.product_id}||${it.size || ""}`;
                    return (
                      <tr key={i} className="align-top">
                        <td className="py-2 text-slate-200">{it.name}<p className="text-xs text-slate-500 font-mono">{it.sku} · {it.size} · {it.color}</p></td>
                        <td className="text-center text-slate-300">{it.qty}</td>
                        <td className="text-right font-mono text-slate-400">{inr(it.unit_price)}</td>
                        <td className="text-right font-mono text-slate-100">{inr(it.unit_price * it.qty - (it.line_discount || 0))}</td>
                        {(returnMode || exchangeMode) && (
                          <td className="text-right">
                            <input type="number" min={0} max={it.qty} value={returnItems[key]?.qty || 0} onChange={(e) => setReturnItems((r) => ({ ...r, [key]: { ...(r[key] || {}), qty: e.target.value } }))} className="w-14 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 font-mono text-right" placeholder="0" />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Edit form fields */}
              {editMode && editDraft && (
                <div className="border-t border-slate-800 pt-4 space-y-3 text-sm">
                  <p className="text-xs text-slate-500 uppercase tracking-widest">Editable fields</p>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Customer name</span>
                      <input value={editDraft.walkin_name} onChange={(e) => setEditDraft({ ...editDraft, walkin_name: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-slate-200" data-testid="edit-cust-name" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Phone</span>
                      <input value={editDraft.walkin_phone} onChange={(e) => setEditDraft({ ...editDraft, walkin_phone: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-slate-200 font-mono" data-testid="edit-cust-phone" />
                    </label>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Discount</p>
                    <div className="flex gap-2 items-center">
                      <select value={editDraft.discount_type} onChange={(e) => setEditDraft({ ...editDraft, discount_type: e.target.value })} className="bg-slate-950 border border-slate-800 rounded px-2 py-2 text-slate-200 font-mono text-xs uppercase" data-testid="edit-discount-type">
                        <option value="none">None</option>
                        <option value="flat">₹ Flat</option>
                        <option value="percent">% Off</option>
                      </select>
                      {editDraft.discount_type !== "none" && (
                        <input type="number" value={editDraft.discount_value} onChange={(e) => setEditDraft({ ...editDraft, discount_value: e.target.value })} className="flex-1 bg-slate-950 border border-slate-800 rounded px-3 py-2 text-slate-200 font-mono" data-testid="edit-discount-value" />
                      )}
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-xs text-slate-400">
                    <input type="checkbox" checked={editDraft.gst_enabled} onChange={(e) => setEditDraft({ ...editDraft, gst_enabled: e.target.checked })} data-testid="edit-gst" /> Apply GST
                    {editDraft.gst_enabled && (
                      <>
                        <input type="number" value={editDraft.gst_rate} onChange={(e) => setEditDraft({ ...editDraft, gst_rate: e.target.value })} className="w-16 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-slate-200 font-mono" placeholder="%" />
                        <input value={editDraft.gstin} onChange={(e) => setEditDraft({ ...editDraft, gstin: e.target.value })} className="flex-1 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-slate-200 font-mono uppercase" placeholder="GSTIN" />
                      </>
                    )}
                  </label>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Payments</p>
                    <div className="space-y-1.5">
                      {editDraft.payments.map((p, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <select value={p.mode} onChange={(e) => setEditDraft({ ...editDraft, payments: editDraft.payments.map((x, j) => j === i ? { ...x, mode: e.target.value } : x) })} className="bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-xs text-slate-200 uppercase font-mono">
                            {PAYMENT_MODES.map((m) => <option key={m}>{m}</option>)}
                          </select>
                          <input type="number" value={p.amount} onChange={(e) => setEditDraft({ ...editDraft, payments: editDraft.payments.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) })} className="flex-1 bg-slate-950 border border-slate-800 rounded px-3 py-1.5 text-slate-200 font-mono" />
                          {editDraft.payments.length > 1 && (
                            <button onClick={() => setEditDraft({ ...editDraft, payments: editDraft.payments.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-red-400"><X size={13} /></button>
                          )}
                        </div>
                      ))}
                      <button onClick={() => setEditDraft({ ...editDraft, payments: [...editDraft.payments, { mode: "upi", amount: 0 }] })} className="text-xs text-slate-500 hover:text-amber-400">+ Split payment</button>
                    </div>
                  </div>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Notes</span>
                    <textarea rows={2} value={editDraft.notes} onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-slate-300 resize-none" />
                  </label>
                </div>
              )}

              {/* Totals */}
              <div className="border-t border-slate-800 pt-3 text-sm font-mono space-y-1">
                {(() => {
                  const t = editMode ? previewTotals : selected;
                  if (!t) return null;
                  return (
                    <>
                      <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{inr(t.subtotal)}</span></div>
                      {t.discount > 0 && <div className="flex justify-between text-amber-400"><span>Discount</span><span>− {inr(t.discount)}</span></div>}
                      {(editMode ? editDraft.gst_enabled : selected.gst_enabled) && (<>
                        <div className="flex justify-between text-slate-500"><span>CGST</span><span>{inr(t.cgst)}</span></div>
                        <div className="flex justify-between text-slate-500"><span>SGST</span><span>{inr(t.sgst)}</span></div>
                      </>)}
                      <div className="flex justify-between text-lg text-slate-100 font-bold pt-2 border-t border-slate-800"><span>TOTAL</span><span>{inr(t.total)}</span></div>
                      {t.paid !== undefined && <div className="flex justify-between text-xs text-slate-500 pt-1"><span>Paid</span><span>{inr(t.paid)}</span></div>}
                      {t.balance > 0 && <div className="flex justify-between text-xs text-amber-400"><span>Balance</span><span>{inr(t.balance)}</span></div>}
                    </>
                  );
                })()}
              </div>

              {returnMode && (
                <button onClick={submitReturn} className="w-full py-3 bg-red-500 hover:bg-red-400 text-white rounded font-medium" data-testid="pos-return-submit">Process return &amp; restore stock</button>
              )}

              {exchangeMode && (
                <div className="border-t border-slate-800 pt-4 space-y-3" data-testid="pos-exchange-panel">
                  <p className="text-xs text-slate-500 uppercase tracking-widest">Replacement items</p>
                  <div className="relative">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input value={exSearch} onChange={(e) => runExSearch(e.target.value)} placeholder="Search product to add as replacement" className="w-full bg-slate-950 border border-slate-800 rounded pl-9 pr-3 py-2.5 text-sm text-slate-200" data-testid="pos-exchange-search" />
                    {exResults.length > 0 && (
                      <div className="absolute z-10 left-0 right-0 mt-1 bg-slate-900 border border-slate-800 rounded max-h-56 overflow-y-auto divide-y divide-slate-800">
                        {exResults.map((p) => (
                          <button key={p.id} onClick={() => addNewItem(p)} className="w-full flex items-center gap-3 p-2.5 hover:bg-slate-800 text-left" data-testid={`pos-exchange-add-${p.sku || p.slug}`}>
                            <div className="flex-1 min-w-0"><p className="text-sm text-slate-100 truncate">{p.name}</p><p className="text-xs text-slate-500 font-mono">{p.sku} · {inr(p.price)}</p></div>
                            <Plus size={15} className="text-amber-500" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {newItems.map((n, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded p-2">
                      <div className="flex-1 min-w-0"><p className="text-sm text-slate-200 truncate">{n.name}</p><p className="text-xs text-slate-500 font-mono">{n.sku}</p></div>
                      {selected.items && <input value={n.size} onChange={(e) => setNewItems((arr) => arr.map((x, j) => j === i ? { ...x, size: e.target.value } : x))} placeholder="Size" className="w-16 bg-slate-900 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 text-center" />}
                      <input type="number" min={1} value={n.qty} onChange={(e) => setNewItems((arr) => arr.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} className="w-12 bg-slate-900 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 font-mono text-center" />
                      <span className="font-mono text-slate-300 text-sm w-20 text-right">{inr(n.unit_price * n.qty)}</span>
                      <button onClick={() => setNewItems((arr) => arr.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400"><X size={14} /></button>
                    </div>
                  ))}

                  <div className="text-sm font-mono space-y-1 border-t border-slate-800 pt-3">
                    <div className="flex justify-between text-slate-500"><span>Returning</span><span>− {inr(exReturnValue)}</span></div>
                    <div className="flex justify-between text-slate-500"><span>New items</span><span>+ {inr(exNewValue)}</span></div>
                    {(() => {
                      const diff = exNewValue - exReturnValue;
                      return (
                        <div className={`flex justify-between font-bold pt-1 border-t border-slate-800 ${diff >= 0 ? "text-amber-400" : "text-emerald-400"}`}>
                          <span>{diff > 0 ? "Customer pays" : diff < 0 ? "Refund to customer" : "Even swap"}</span>
                          <span>{inr(Math.abs(diff))}</span>
                        </div>
                      );
                    })()}
                  </div>
                  <button onClick={submitExchange} className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded font-semibold" data-testid="pos-exchange-submit">Process exchange</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {printing && selected && <PrintableBill bill={selected} settings={settings} />}

      {/* Void confirmation modal */}
      {voidPrompt && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-3 sm:p-6" onClick={() => setVoidPrompt(null)} data-testid="pos-void-modal">
          <div className="bg-slate-900 border border-orange-500/30 rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-800 flex items-center gap-3">
              <Ban className="text-orange-400" size={20} />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-slate-100">Void bill <span className="font-mono text-amber-400">{voidPrompt.bill.bill_no}</span>?</h2>
                <p className="text-xs text-slate-500">Bill number stays in the ledger with a "voided" tag. Stock is restored. It will be excluded from all reports.</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Reason (optional)</span>
                <input
                  autoFocus
                  value={voidPrompt.reason}
                  onChange={(e) => setVoidPrompt({ ...voidPrompt, reason: e.target.value })}
                  placeholder="e.g. Customer changed mind, wrong item, cash returned"
                  className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200"
                  data-testid="pos-void-reason"
                />
              </label>
              <div className="flex gap-2">
                <button onClick={() => setVoidPrompt(null)} className="flex-1 py-2.5 border border-slate-700 rounded text-slate-300 hover:text-slate-100 text-sm">Cancel</button>
                <button onClick={submitVoid} className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-400 text-slate-950 rounded font-semibold text-sm" data-testid="pos-void-submit">Void bill</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
